import { useMemo } from "react";
import type { DocFieldSchema } from "@/lib/erp/meta";
import { evaluateDependsOn } from "@/lib/erp/meta/utils/dependsOnEvaluator";

type BoolMap = Record<string, boolean>;

interface FieldDependencies {
  visibleFieldNames: Set<string>;
  fieldReadOnly: BoolMap;
  fieldRequired: BoolMap;
}

/**
 * Compute field dependencies (visibility, read-only, required)
 * Uses useMemo to avoid recomputation on every render
 *
 * @param parentDoc - Parent document data (for child table fields that use parent.* refs)
 */
export function useFieldDependencies(
  fields: DocFieldSchema[],
  formData: Record<string, any>,
  parentDoc?: Record<string, any>
): FieldDependencies {
  return useMemo(
    () => computeDependencies(fields, formData, parentDoc),
    [fields, formData, parentDoc]
  );
}

/**
 * Core dependency computation logic
 *
 * CRITICAL: mandatory_depends_on and read_only_depends_on must explicitly check
 * if the expression exists before evaluating. If undefined, they default to false,
 * not true (which would make all fields required/read-only).
 *
 * Each expression type passes its context so parse errors fail safely:
 * - depends_on errors → field stays visible (safe)
 * - read_only_depends_on errors → field stays editable (safe)
 * - mandatory_depends_on errors → field stays optional (safe)
 */
function computeDependencies(
  fields: DocFieldSchema[],
  doc: Record<string, any>,
  parent?: Record<string, any>
): FieldDependencies {
  const fieldReadOnly: BoolMap = {};
  const fieldRequired: BoolMap = {};
  const visibleFieldNames = new Set<string>();

  for (const field of fields) {
    // Base visibility from schema
    const baseVisible = field.hidden !== 1;

    // Dynamic visibility from depends_on
    // evaluateDependsOn returns true for undefined (field visible by default)
    const visible = baseVisible && evaluateDependsOn(field.depends_on, doc, parent, "visibility");

    if (visible) {
      visibleFieldNames.add(field.fieldname);

      // Read-only: base + read_only_depends_on
      const baseReadOnly = field.read_only === 1;
      const dynamicReadOnly = field.read_only_depends_on
        ? evaluateDependsOn(field.read_only_depends_on, doc, parent, "read_only")
        : false;
      fieldReadOnly[field.fieldname] = baseReadOnly || dynamicReadOnly;

      // Required: base + mandatory_depends_on
      const baseRequired = field.reqd === 1;
      const dynamicRequired = field.mandatory_depends_on
        ? evaluateDependsOn(field.mandatory_depends_on, doc, parent, "mandatory")
        : false;
      fieldRequired[field.fieldname] = baseRequired || dynamicRequired;
    }
  }

  return { visibleFieldNames, fieldReadOnly, fieldRequired };
}
